import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  GridApi,
  ICellEditorParams,
  ValueFormatterParams,
  ValueSetterParams,
} from "ag-grid-community";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../components/utils";
import { formatDisplayDate } from "../../utils/date";
import {
  useConfirmBulkExtendExercise,
  useValidateBulkExtendExerciseRows,
} from "../../queries/esopPlan";
import { ParsedLotExtendRow } from "../../types/BulkExtendExercise";
import SearchInput from "../../components/shared/SearchInput";

interface BulkExtendExercisePreviewProps {
  data: ParsedLotExtendRow[];
  onCancel: () => void;
}

type GroupBy = "none" | "grantIdentifier" | "employeeId";

const groupByOptions: { value: GroupBy; label: string }[] = [
  { value: "none", label: "None" },
  { value: "grantIdentifier", label: "Grant" },
  { value: "employeeId", label: "Employee" },
];

const ValidationCellRenderer = (params: any) => {
  const row: ParsedLotExtendRow = params.data;
  if (!row) return null;

  if (row.isValid) {
    return (
      <div className="flex items-center gap-1 text-green-600">
        <Icon icon="mdi:check-circle" height={16} />
        <span className="text-xs">Valid</span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1 text-red-600"
      title={row.errors.join(", ")}
    >
      <Icon icon="mdi:alert-circle" height={16} />
      <span className="text-xs">Error</span>
    </div>
  );
};

const ErrorsCellRenderer = (params: any) => {
  const row: ParsedLotExtendRow = params.data;
  if (!row || !row.errors || row.errors.length === 0) {
    return <span className="text-gray-400">-</span>;
  }

  return (
    <div className="text-xs text-red-600 py-1">
      <ul className="list-disc list-inside">
        {row.errors.map((err, idx) => (
          <li key={idx}>{err}</li>
        ))}
      </ul>
    </div>
  );
};

const BulkExtendExercisePreview: React.FC<BulkExtendExercisePreviewProps> = ({
  data,
  onCancel,
}) => {
  const navigate = useNavigate();
  const gridApi = useRef<GridApi | any>(null);
  const [rowData, setRowData] = useState<ParsedLotExtendRow[]>(data);
  const [groupBy, setGroupBy] = useState<GroupBy>("none");
  const [errorMessage, setErrorMessage] = useState("");

  const { mutate: confirmExtend, isLoading: isConfirming } =
    useConfirmBulkExtendExercise();
  const { mutate: validateRows, isLoading: isValidating } =
    useValidateBulkExtendExerciseRows();

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    setTimeout(() => params.api.sizeColumnsToFit(), 100);
  };

  useEffect(() => {
    if (!gridApi.current) return;
    setTimeout(() => gridApi.current?.sizeColumnsToFit(), 0);
  }, [groupBy]);

  const valueSetter = (params: ValueSetterParams): boolean => {
    const field = params.colDef.field;
    if (!field) return false;
    if ((params.data as any)[field] === params.newValue) return false;
    (params.data as any)[field] = params.newValue;
    return true;
  };

  const dateCellEditorSelector = (_params: ICellEditorParams) => ({
    component: "agDateCellEditor",
    params: { min: "1900-01-01", max: "2100-12-31" },
  });

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Status",
        field: "isValid",
        width: 110,
        minWidth: 110,
        pinned: "left",
        cellRenderer: ValidationCellRenderer,
        valueGetter: (params) => (params.data?.isValid ? "Valid" : "Invalid"),
        filter: "agSetColumnFilter",
        filterParams: { values: ["Valid", "Invalid"] },
      },
      {
        headerName: "Grant Identifier",
        field: "grantIdentifier",
        minWidth: 170,
        editable: false,
        filter: "agSetColumnFilter",
        rowGroup: groupBy === "grantIdentifier",
        hide: groupBy === "grantIdentifier",
      },
      {
        headerName: "Employee Id",
        field: "employeeId",
        minWidth: 160,
        editable: false,
        filter: "agSetColumnFilter",
        rowGroup: groupBy === "employeeId",
        hide: groupBy === "employeeId",
      },
      {
        headerName: "Employee Name",
        field: "employeeName",
        minWidth: 180,
        editable: false,
        filter: "agSetColumnFilter",
      },
      {
        headerName: "Vesting Lot Id",
        field: "lotId",
        minWidth: 220,
        editable: false,
      },
      {
        headerName: "Options getting Extended",
        field: "remainingBalance",
        minWidth: 120,
        editable: false,
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
      },
      {
        headerName: "Current Expiry Date",
        field: "currentExpiryDate",
        minWidth: 160,
        editable: false,
        filter: "agDateColumnFilter",
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.node?.group) return "";
          return params.value ? formatDisplayDate(params.value) : "Never";
        },
      },
      {
        headerName: "New Expiry Date",
        field: "newExpiryDate",
        minWidth: 160,
        editable: true,
        valueSetter,
        cellEditorSelector: dateCellEditorSelector,
        valueGetter: (params) => {
          const val = params.data?.newExpiryDate;
          if (!val) return null;
          const date = new Date(val);
          return Number.isNaN(date.getTime()) ? null : date;
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.node?.group) return "";
          return params.value ? formatDisplayDate(params.value) : "-";
        },
      },
      {
        headerName: "Errors",
        field: "errors",
        minWidth: 260,
        flex: 1,
        editable: false,
        cellRenderer: ErrorsCellRenderer,
        autoHeight: true,
        wrapText: true,
      },
      {
        headerName: "Vested",
        field: "vested",
        minWidth: 110,
        editable: false,
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
      },
      {
        headerName: "Exercised",
        field: "exercised",
        minWidth: 110,
        editable: false,
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
      },
      {
        headerName: "Cashed Out",
        field: "cashedOut",
        minWidth: 110,
        editable: false,
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
      },
      {
        headerName: "Lapsed",
        field: "lapsed",
        minWidth: 110,
        editable: false,
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
      },
      {
        headerName: "Forfeited",
        field: "forfeited",
        minWidth: 110,
        editable: false,
        filter: "agNumberColumnFilter",
        aggFunc: "sum",
      },
    ],
    [groupBy]
  );

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      minWidth: 220,
      cellRendererParams: { suppressCount: false },
    }),
    []
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      menuTabs: ["filterMenuTab"] as any,
      filterParams: { buttons: ["reset"] },
      cellStyle: { paddingTop: "12px", lineHeight: "20px" },
    }),
    []
  );

  const handleValidate = () => {
    setErrorMessage("");
    validateRows(rowData, {
      onSuccess: (result) => {
        if (result.data) {
          setRowData(result.data);
          toast.success("Validation completed");
        }
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.errorRaw ||
          error?.response?.data?.errorMessage ||
          "Error validating rows";
        setErrorMessage(message);
        toast.error("Error validating rows");
      },
    });
  };

  const handleConfirm = () => {
    if (!rowData.every((row) => row.isValid)) {
      toast.error("Please fix all errors before confirming.");
      return;
    }

    setErrorMessage("");
    confirmExtend(rowData, {
      onSuccess: (result) => {
        if (result.data) {
          setRowData(result.data);
        }
        const errorCount = result.errors?.length || 0;
        toast(
          errorCount > 0
            ? `Extended exercise for ${result.updated} lot(s). ${errorCount} lot(s) had errors.`
            : `Extended exercise for ${result.updated} lot(s).`,
          { type: result.updated > 0 ? "success" : "warning", autoClose: 3000 }
        );
        if (result.updated > 0 && errorCount === 0) {
          navigate("/options/allPlans");
        }
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.errorRaw ||
          error?.response?.data?.errorMessage ||
          "Error extending exercise";
        setErrorMessage(message);
        toast.error("Error extending exercise");
      },
    });
  };

  const validCount = rowData.filter((row) => row.isValid).length;
  const invalidCount = rowData.filter((row) => !row.isValid).length;

  return (
    <VStack className="w-full bg-white border border-gray-200 rounded-lg min-h-[600px]">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Preview Bulk Extend Exercise
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Review and edit new expiry dates before confirming. Only valid rows
            can be confirmed.
          </p>
        </div>
        <HStack className="items-center gap-4">
          <SearchInput
            placeholder="Search..."
            onChange={(e: any) => {
              if (gridApi.current) {
                gridApi.current.setQuickFilter(e.target.value);
              }
            }}
          />
        </HStack>
      </div>

      {errorMessage && (
        <div className="mx-6 mt-4 p-2 text-red-600 bg-red-100 border border-red-400 rounded">
          <ul>
            {errorMessage.split("\n").map((line, index) => (
              <li key={index}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      <HStack className="flex items-center gap-6 px-6 py-3 bg-gray-50 border-b border-gray-100 justify-between">
        <HStack className="gap-2">
          <div className="flex items-center gap-1">
            <span className="text-sm text-gray-600">Total Rows:</span>
            <span className="font-semibold text-gray-800">
              {rowData.length}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Icon
              icon="mdi:check-circle"
              className="text-green-600"
              height={16}
            />
            <span className="text-sm text-gray-600">Valid:</span>
            <span className="font-semibold text-green-600">{validCount}</span>
          </div>
          <div className="flex items-center gap-1">
            <Icon
              icon="mdi:alert-circle"
              className="text-red-600"
              height={16}
            />
            <span className="text-sm text-gray-600">Invalid:</span>
            <span className="font-semibold text-red-600">{invalidCount}</span>
          </div>
        </HStack>
      </HStack>

      <div className="px-6 py-2 border-b border-gray-100 bg-white">
        <HStack className="items-center gap-2">
          <span className="text-xs text-gray-500 font-medium">Group by:</span>
          {groupByOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setGroupBy(opt.value)}
              className={`text-xs px-3 py-1 rounded border transition-colors ${
                groupBy === opt.value
                  ? "bg-orange-501 text-white border-orange-501"
                  : "bg-white text-gray-600 border-gray-300 hover:border-orange-501 hover:text-orange-501"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </HStack>
      </div>

      <div
        className="bulk-state-grid ag-theme-material w-full px-4 py-4"
        style={{ height: "500px", width: "100%" }}
      >
        <AgGridReact
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDisplayType="singleColumn"
          groupDefaultExpanded={groupBy === "none" ? undefined : 0}
          suppressAggFuncInHeader
          onGridReady={onGridReady}
          rowData={rowData}
          rowClass="border-t border-dashed"
          stopEditingWhenCellsLoseFocus={true}
          singleClickEdit={true}
          getRowStyle={(params) =>
            params.data && !params.data.isValid
              ? { backgroundColor: "#fef2f2" }
              : undefined
          }
        />
      </div>

      <HStack className="justify-between items-center gap-4 px-6 py-4 border-t border-gray-100">
        <ButtonPrimary1
          className="text-red-500 border-red-500 hover:bg-red-50"
          onClick={onCancel}
          disabled={isConfirming || isValidating}
        >
          Discard & Back
        </ButtonPrimary1>
        <HStack className="gap-3">
          <ButtonPrimary1
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
            onClick={handleValidate}
            loading={isValidating}
            disabled={isConfirming}
          >
            Validate All
          </ButtonPrimary1>
          <ButtonPrimary
            onClick={handleConfirm}
            disabled={
              rowData.every((row) => row.isValid) === false || isValidating
            }
            loading={isConfirming}
          >
            Confirm & Extend
          </ButtonPrimary>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default BulkExtendExercisePreview;
